import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronRightIcon } from "lucide-react";
import dayjs from "dayjs";
import { UserContext, getCampaignsByBookerId, getUserById } from "../services/firebase";
import { Campaign, User } from "../shared/types";

import Layout from "../components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Heading, Overline } from "@/components/typography";
import { Skeleton } from "@/components/ui/skeleton";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";

const Campaigns: React.FC = () => {
    const { uid } = useParams<{ uid: string }>();
    const authenticatedUser = useContext(UserContext);
    const [user, setUser] = useState<User>();
    const [userCampaigns, setUserCampaigns] = useState<Campaign[]>();
    const [campaignsLoading, setCampaignsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Ignore stale results if uid/auth changes mid-fetch (quick navigation).
        let cancelled = false;

        getInitialProps();

        async function getInitialProps() {
            if (!authenticatedUser) {
                return;
            }

            setCampaignsLoading(true);
            try {
                const theUserCampaigns = await getCampaignsByBookerId(uid || authenticatedUser.id!, false);
                if (cancelled) {
                    return;
                }
                setUserCampaigns(theUserCampaigns.sort((c1, c2) => (c2.docMeta?.createTime ? c2.docMeta?.createTime.toDate().getTime() : 0) - (c1.docMeta?.createTime ? c1.docMeta?.createTime.toDate().getTime() : 0)));

                if (uid) {
                    const newUser = await getUserById(uid);
                    if (cancelled) {
                        return;
                    }
                    setUser(newUser);
                } else {
                    setUser(undefined);
                }
            } finally {
                if (!cancelled) {
                    setCampaignsLoading(false);
                }
            }
        }

        return () => {
            cancelled = true;
        };
    }, [authenticatedUser, uid]);

    const phase = useSkeletonPhases(campaignsLoading);

    return (
        <Layout gray={true}>
            <section className="mx-auto max-w-lg p-4">
                <Heading type="h1" className="mt-8 mb-8">
                    {user ? `${user.firstName} ${user.lastName}'s Campaigns` : "My Campaigns"}
                </Heading>

                {phase === "showSkeleton" ? (
                    Array.from({ length: 4 }, (_, index) => <CampaignRowSkeleton key={index} />)
                ) : phase === "showResult" && (
                    userCampaigns?.length ? (
                        userCampaigns.map((campaign) => (
                            <Card
                                key={campaign.id}
                                size="sm"
                                className="mb-4 cursor-pointer gap-0 py-4"
                                onClick={() => navigate(`/campaign/${campaign.id}`)}
                            >
                                {/* Campaign name + meta vs chevron */}
                                <CardContent className="flex items-center justify-between gap-4">
                                    <div className="space-y-1">
                                        <Heading type="h4">{campaign.campaignName}</Heading>
                                        {campaign.docMeta?.createTime ? (
                                            <div>
                                                <Overline tag="span" className="mr-1">Created:</Overline>
                                                {dayjs(campaign.docMeta.createTime.toDate()).format("MMM DD, YYYY")}
                                            </div>
                                        ) : null}

                                        {campaign?.bookings ? (
                                            <div>
                                                <Overline tag="span" className="mr-1">Bookings:</Overline>
                                                {Object.keys(campaign.bookings).length}
                                            </div>
                                        ) : null}
                                    </div>
                                    <ChevronRightIcon className="size-5 shrink-0 text-muted-foreground" aria-hidden />
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <div>No Recent Campaigns</div>
                    )
                )}
            </section>
        </Layout>
    );
};

/** Name, meta, and chevron rhythm for a campaign-row wait. */
function CampaignRowSkeleton() {
    return (
        <Card size="sm" className="mb-4 gap-0 py-4">
            <CardContent className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="size-5 shrink-0" />
            </CardContent>
        </Card>
    );
}

export default Campaigns;
